<template>
  <div class="view">
    <v-breadcrumbs>
      <v-breadcrumbs-item :exact="true" :to="{ name: 'home', params: {} }"
        >Home</v-breadcrumbs-item
      >
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item :exact="true" :to="{ name: 'student', params: {} }">
        Student
      </v-breadcrumbs-item>
      <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
      <v-breadcrumbs-item :to="{ name: 'studentView', params: {} }">
        View Student
      </v-breadcrumbs-item>
    </v-breadcrumbs>

    <v-card v-if="loaded">
      <v-card-title>
        View Student
        <v-snackbar
          v-if="!student.active"
          content-class="text-center"
          color="warning"
          outlined
          :timeout="-1"
          width="100px"
          :value="true"
          absolute
          top
          centered
          >Inactive Student</v-snackbar
        >
        <v-snackbar
          content-class="text-center"
          color="warning"
          outlined
          type="warning"
          :timeout="-1"
          width="100px"
          :value="true"
          absolute
          top
          centered
          v-if="student.active && studentMedicals && studentMedicals.length > 0"
        >
          <div v-ripple style="cursor: pointer" @click="assignMedicalAlert">
            <div
              v-for="studentMedical in studentMedicals"
              :key="studentMedical.id"
            >
              {{ studentMedical.medicalAlertName }}
              <span v-if="studentMedical.medicalAlertOptionId">
                - {{ studentMedical.medicalAlertOptionName }}</span
              >
            </div>
          </div>
        </v-snackbar>
      </v-card-title>
      <v-card-text>
        <div
          class="
            d-flex
            flex-column flex-sm-row
            justify-space-between
            text-subtitle-2
            my-2
          "
        >
          <div>
            {{ student.firstName }} {{ student.lastName }}
            <span v-if="ageGender">({{ ageGender }})</span>
            
            <v-tooltip v-if="student.memo" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="primary" dark v-bind="attrs" v-on="on" class="mx-2" icon>
                  <v-icon> mdi-note-outline </v-icon>
                </v-btn>
              </template>
              <span>{{student.memo}}</span>
            </v-tooltip>

            <v-btn
              class="mx-2"
              icon
              :to="{ name: 'studentEdit', params: { id: student.id } }"
              ><v-icon>mdi-account-edit-outline</v-icon></v-btn
            >

            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  :to="{
                    name: 'studentCreate',
                    query: { siblingId: student.id },
                  }"
                >
                  <v-list-item-title>Add Sibling</v-list-item-title>
                </v-list-item>
                <v-list-item @click="viewGuardian">
                  <v-list-item-title>View Guardians</v-list-item-title>
                </v-list-item>
                <v-list-item
                  :disabled="!student.active"
                  @click="assignMedicalAlert"
                >
                  <v-list-item-title>Edit Medical Alert</v-list-item-title>
                </v-list-item>
                <v-list-item
                  :disabled="!student.active"
                  @click="assignClassroom"
                >
                  <v-list-item-title>{{
                    student.classroomId ? "Edit Classroom" : "Assign Classroom"
                  }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="removeStudent">
                  <v-list-item-title>Delete Student</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <div class="text-caption">
              <span
                v-if="globalCenter && !globalCenter.id"
                class="text-caption"
              >
                {{ getCenterName(student.centerId) }} -
              </span>
              <span v-if="student.classroomName" class="text-caption">
                {{ student.classroomName }} -
              </span>

              {{ student.contactTypeName }}
              <span v-if="student.clientStudentId">
                <v-divider class="mx-2" vertical />
                ID: {{ student.clientStudentId }}
              </span>
            </div>
          </div>
          <div class="my-auto mt-2 my-sm-0 text-caption">
            <v-icon color="orange darken-1" v-if="isBirthday" class="mb-2"
              >mdi-cake-variant-outline</v-icon
            >
            DoB:
            <span v-if="student.dob">{{ student.dob | formatDateYear }} </span>
            <span v-else>
              <router-link
                :to="{ name: 'studentEdit', params: { id: student.id } }"
              >
                n/a
              </router-link>
            </span>
          </div>
        </div>
        <v-divider />

        <div class="d-flex justify-space-between">
          <div class="mt-5">{{ getStudentRequirementStatus() }}</div>
          <v-switch
            :disabled="!student.active"
            dense
            hide-details
            v-model="multiSubmitMode"
            :label="`Submit Multiple: ${multiSubmitMode ? 'Yes' : 'No'}`"
          ></v-switch>
        </div>

        <v-data-table
          id="student-view"
          class="my-5"
          :mobile-breakpoint="0"
          :hide-default-footer="true"
          :hide-default-header="true"
          :headers="headers"
          :items="requirements"
          disable-pagination
          group-by="type"
        >
          <template v-slot:group.header="{ group, headers }">
            <td :colspan="headers.length">
              <div class="d-flex justify-space-between">
                <div class="font-weight-bold my-auto">{{ group }}</div>
                <v-alert
                  v-if="group == 'Vaccination' && !student.dob"
                  class="mt-2"
                  outlined
                  dense
                  type="warning"
                >
                  Student's birthday is required
                </v-alert>

                <v-checkbox
                  v-if="multiSubmitMode && hasIncomplete(group)"
                  v-model="toggle[group]"
                  class="mr-4"
                  @change="toggleGroup(group)"
                />
              </div>
            </td>
          </template>
          <template v-slot:item.name="{ item }">
            <div class="d-flex">
              <div v-if="item.formId" class="mr-2">
                <v-btn
                  color="primary"
                  target="_blank"
                  icon
                  :href="getFormLink(item.formId)"
                  :title="getFormName(item.formId)"
                  ><v-icon>mdi-form-select</v-icon></v-btn
                >
              </div>
              <div class="my-auto">
                <div>
                  {{ item.name }} <span v-if="!item.required">(Optional)</span>
                </div>
                <div class="text-caption">
                  <span v-if="item.attachmentRequired"
                    ><small>Require proof</small></span
                  >
                </div>
              </div>
              <!-- <attachments-view class="ml-1" v-if="item.attachments && item.attachments.length > 0" v-model="item.attachments" :inline="false"/> -->
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <v-list v-if="item.status && item.status.length > 0">
              <template v-for="(s, index) in item.status">
                <v-list-item :key="`${s.id}_${index}`">
                  <v-list-item-content>
                    <student-requirement-status
                      :key="ts"
                      :disabled="!student.active"
                      v-model="item.status[index]"
                      :requirement="item"
                      :student="student"
                      :selectMode="multiSubmitMode"
                      @selectionChanged="submissionSelectionChanged"
                      @waiveRequirement="showWaiveForm"
                      @statusChanged="initView"
                    />
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list>
            <div v-else class="text-weight-light mx-4">No status</div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <div class="d-flex justify-space-between ma-5">
      <v-btn @click="goBack">Back</v-btn>
      <!-- <v-btn @click="edit(id)" color="primary">Edit</v-btn> -->
      <span></span>
    </div>

    <v-snackbar timeout="-1" color="primary" v-model="multiSubmitMode">
      {{ selected.length }} requirements selected
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          :disabled="selected.length < 1"
          @click="submitMultiple"
        >
          Submit
        </v-btn>
      </template>
    </v-snackbar>

    <v-dialog width="300" v-model="guardianDialog">
      <v-sheet class="pa-6">
        <div class="d-flex justify-end">
          <v-btn @click="guardianDialog = null" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <student-guardians :student="student" :key="student.id" />
      </v-sheet>
    </v-dialog>
    <v-dialog v-model="classroomDialog">
      <v-sheet class="pa-6">
        <div class="d-flex justify-end">
          <v-btn @click="classroomDialog = null" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <student-classroom
          @saved="classroomChanged"
          @cancel="classroomDialog = null"
          :student="student"
          :key="student.id"
        />
      </v-sheet>
    </v-dialog>
    <v-dialog v-model="medicalDialog">
      <v-sheet class="pa-6">
        <div class="d-flex justify-end">
          <v-btn @click="medicalDialog = null" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <student-medical-alert
          @change="initMedicalAlert"
          :student="student"
          :key="student.id"
        />
      </v-sheet>
    </v-dialog>
    <confirmation-dialog ref="confirm" />
  </div>
</template>

<script>
import { config } from '@/config'
import { mapActions, mapGetters } from "vuex";
import { getStudent } from "@/services/student";
import StudentRequirementStatus from "./StudentRequirementStatus.vue";
import { getStudentRequirements } from "@/services/requirement";
import moment from "moment";
const StudentGuardians = () => import("@/components/rp/StudentGuardians.vue");
const StudentClassroom = () =>
  import("@/components/student/StudentClassroom.vue");
const StudentMedicalAlert = () =>
  import("@/components/student/StudentMedicalAlert.vue");
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");
import { parseDomain } from "parse-domain";
import { fetchStudentMedicalAlerts } from "@/services/medicalAlertServices";

export default {
  components: {
    StudentRequirementStatus,
    ConfirmationDialog,
    StudentGuardians,
    StudentClassroom,
    StudentMedicalAlert,
  },
  data() {
    return {
      id: "",
      multiSubmitMode: false,
      toggle: {},
      selected: [],
      student: {},
      loaded: false,
      relations: {},
      requirements: [],
      studentMedicals: [],
      forms: [],
      ts: Date.now(),
      guardianDialog: false,
      classroomDialog: false,
      medicalDialog: false,
      headers: [
        {
          text: "Name",
          value: "name",
          align: "start",
          sortable: true,
        },
        {
          text: "Status",
          value: "status",
          align: "start",
          sortable: true,
        },
      ],
    };
  },
  computed: {
    ...mapGetters("auth", ["myCenters"]),
    ...mapGetters("navigation", ["isAppLoading", "globalCenter"]),

    ageGender() {
      let info = "";
      if (this.student.dob) {
        const ageYears = moment().diff(this.student.dob, "years", false);
        const ageMonths = moment().diff(this.student.dob, "months", false) % 12;
        // const age = new AgeFromDateString(this.student.dob).age;
        info += `${ageYears}y${ageMonths}m`;
      }
      if (this.student.gender) {
        if (info) {
          info += ` ${this.student.gender} `;
        } else {
          info = this.student.gender;
        }
      }
      return info;
    },
    isBirthday() {
      if (this.student.dob) {
        const dob = moment(this.student.dob).format("MMDD");
        const today = moment().format("MMDD");
        return dob === today;
      } else {
        return false;
      }
    },
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("student", ["deleteStudent"]),
    goBack() {
      this.$router.go(-1);
    },
    edit(id) {
      this.$router.push({ name: "studentEdit", params: { id: id } });
    },
    setInstance() {
      this.setDependencies().then(() => {
        getStudent(this.id)
          .then((response) => {
            this.student = response.data;
            this.loaded = true;
          })
          .catch(() => {
            this.addErrorMessage("Error loading student");
            this.loaded = true;
          });
      });
    },
    async setDependencies() {
      getStudentRequirements([this.id].join(",")).then((response) => {
        this.requirements = response.data;
        if (this.requirements && this.requirements.length > 0) {
          this.requirements.forEach((r) => {
            if (r.status) {
              r.status = Object.values(r.status);
            }

            if (r.attachments) {
              r.attachments.forEach((file) => {
                const path = `${config.BASE_URL}/compliance/requirement/attachment/${file.id}`;
                file.path = path;
              });
            }
          });

          //fetch associated forms
          this.initForms();
        }
      });
    },
    getRelationText(id, relationProperty, relationAttribute) {
      const relationList = this.relations[relationProperty];
      if (relationList) {
        const relationObj = relationList.find((r) => r.id == id);
        return relationObj[relationAttribute];
      } else {
        return "";
      }
    },
    submitMultiple() {
      const requirements = this.selected.map((r) => {
        if (r.type == "Vaccination") {
          return `${r.requirementId}_${r.forDoseNumber}`;
        } else {
          return `${r.requirementId}`;
        }
      });
      const route = {
        path: `/studentRequirementSubmission/create`,
        query: {
          studentId: this.student.id,
          requirements: requirements.join(","),
        },
      };
      this.$router.push(route);
    },
    submissionSelectionChanged({ state, requirement, forDoseNumber }) {
      console.log("Selected: ", requirement, forDoseNumber);
      if (state === true) {
        const r = Object.assign({}, requirement);
        r.forDoseNumber = forDoseNumber;
        this.selected.push(r);
      } else {
        const selectedIndex = this.selected.findIndex(
          (r) => r.id == requirement.id
        );
        if (selectedIndex > -1) {
          this.selected.splice(selectedIndex, 1);
        }
      }
    },
    getStudentRequirementStatus() {
      if (this.requirements) {
        let statuses = [];
        this.requirements.forEach((r) => {
          if (r.status) {
            statuses = statuses.concat(Object.values(r.status));
          }
        });
        if (statuses.length > 0) {
          const completedCount = statuses.filter(
            (s) => s.state == "completed" || s.state == "pending"
          ).length;
          const incompleteCount = statuses.filter(
            (s) => s.state == "incomplete"
          ).length;
          const totalCount = completedCount + incompleteCount;
          return `${completedCount} of ${totalCount} requirements completed`;
        } else {
          return "Status: n/a";
        }
      }
    },
    async removeStudent() {
      let confirm = await this.$refs.confirm.open(
        "Confirm Delete",
        "Please confirm you want to delete this student?"
      );
      if (confirm) {
        if (this.student && this.student.id) {
          this.deleteStudent(this.student.id)
            .then(() => {
              this.addSuccessMessage("Student was deleted successfully");
              this.$router.push({ name: "student" });
            })
            .catch(() => {
              this.addErrorMessage("Error deleting student");
            });
        }
      }
    },
    toggleGroup(group) {
      this.selected = [];
      const groupRequirements = this.requirements.filter(
        (r) => r.type == group
      );
      if (this.toggle[group]) {
        groupRequirements.forEach((req) => {
          req.status
            .filter((s) => !s.submissionDate)
            .forEach((status) => {
              status.selected = true;
              const state = status.selected;
              const doseNumber = status.dose ? status.dose : 1;
              this.submissionSelectionChanged({
                state,
                requirement: req,
                forDoseNumber: doseNumber,
              });
            });
        });
      } else {
        groupRequirements.forEach((req) => {
          req.status
            .filter((s) => !s.submissionDate)
            .forEach((status) => {
              status.selected = false;
              const state = status.selected;
              const doseNumber = status.dose ? status.dose : 1;
              this.submissionSelectionChanged({
                state,
                requirement: req,
                forDoseNumber: doseNumber,
              });
            });
        });
      }

      this.ts = Date.now();
    },
    hasIncomplete(group) {
      let incompleteCount = 0;
      const groupRequirements = this.requirements.filter(
        (r) => r.type == group
      );
      groupRequirements.forEach((req) => {
        incompleteCount = req.status
          ? req.status.filter((s) => !s.submissionDate).length
          : 0;
      });

      return incompleteCount > 0 ? true : false;
    },
    getCenterName(id) {
      if (this.myCenters && this.myCenters.length > 0) {
        const center = this.myCenters.find((c) => c.id == id);
        return center ? center.name : "Location N/A";
      } else {
        return "Location N/A";
      }
    },
    viewGuardian() {
      this.guardianDialog = true;
    },
    assignClassroom() {
      this.classroomDialog = true;
    },
    assignMedicalAlert() {
      this.medicalDialog = true;
    },
    async initForms() {
      if (this.requirements) {
        const formIds = this.requirements
          .filter((r) => r.formId)
          .map((r) => r.formId);
        if (formIds && formIds.length > 0) {
          const params = {
            formIds: formIds.join(","),
          };
          return await this.$axios
            .get("/form/get-forms", { params })
            .then((resp) => {
              this.forms = resp.data;
            });
        }
      }
    },
    getFormLink(formId) {
      const onlineForm = this.forms.find((f) => f.id == formId);
      if (onlineForm) {
        if (process.env.NODE_ENV != "development") {
          const parseResult = parseDomain(window.location.hostname);
          const { domain, topLevelDomains } = parseResult;
          const topLevel = topLevelDomains.join(".");

          return `https://form.${domain}.${topLevel}/#/${onlineForm.accountName}/form/${onlineForm.id}`;
        } else {
          return `http://localhost:8086/#/${onlineForm.accountName}/form/${onlineForm.id}`;
        }
      } else {
        return null;
      }
    },
    getFormName(formId) {
      const onlineForm = this.forms.find((f) => f.id == formId);
      return onlineForm ? onlineForm.name : null;
    },
    classroomChanged(classroom) {
      // this.student.classroom = classroom;
      if (classroom) {
        this.$set(this.student, "classroomId", classroom.id);
        this.$set(this.student, "classroomName", classroom.roomNumber);
      }
      this.classroomDialog = null;
    },
    initMedicalAlert() {
      const fetchMedicals = async () => {
        this.studentMedicals = await fetchStudentMedicalAlerts(this.id);
      };
      fetchMedicals();
    },
    initView(){
      this.setInstance();
    }
  },
  created() {
    this.id = this.$route.params.id;

    this.initMedicalAlert();
    this.setInstance();
    this.$emit("showParent", false);
  },
};
</script>
<style>
#student-view > .v-data-table__wrapper > table > tbody > tr:hover {
  background: inherit !important;
}
#student-view
  > .v-data-table__wrapper
  > table
  > tbody
  > tr.v-row-group__header {
  background: #f5f5f5 !important;
}
</style>
